import React from 'react';
import Navigation from '../../components/tulote/Navigation';
import { 
  Building2, 
  MapPin, 
  Upload, 
  FileText, 
  CheckCircle, 
  ArrowRight,
  Mail,
  Phone,
  Globe
} from 'lucide-react';
import { useCountry } from '../../contexts/CountryContext';
import { getUrlWithCountry } from '../../utils/countryUtils';
import brandConfig from '../../config/brandConfig';

function HowToAddProjects() {
  const { country } = useCountry();
  const dashboardUrl = brandConfig.dashboardUrl;
  
  // Helper function to get path with country parameter
  const getPath = (path) => {
    return getUrlWithCountry(path, country);
  };

  const steps = [
    {
      icon: <Building2 className="w-6 h-6" />,
      title: 'Crea tu cuenta',
      description: 'Regístrate en nuestra plataforma para comenzar a gestionar tus proyectos inmobiliarios.',
      action: 'Crear cuenta'
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Crea tu proyecto',
      description: 'Agrega la información básica de tu proyecto: nombre, ubicación, descripción y características principales.',
      action: 'Agregar proyecto'
    },
    {
      icon: <Upload className="w-6 h-6" />,
      title: 'Sube tus lotes',
      description: 'Importa tus lotes desde archivos GeoJSON, Shapefile o KML, o créalos manualmente en el mapa interactivo.',
      action: 'Importar lotes'
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'Configura precios y detalles',
      description: 'Establece precios, características de cada lote, áreas recreativas y puntos de interés.',
      action: 'Configurar proyecto'
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: 'Publica tu proyecto',
      description: 'Una vez configurado, publica tu proyecto para que sea visible en la plataforma y los clientes puedan explorarlo.',
      action: 'Publicar'
    }
  ];

  const features = [
    'Importación masiva de lotes desde archivos geoespaciales',
    'Mapa interactivo para visualización y edición',
    'Gestión completa de clientes y cotizaciones',
    'Sistema de pagos y planes de financiamiento',
    'Reportes y estadísticas en tiempo real',
    'Soporte multi-organización y multi-usuario'
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 via-white to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-20 h-20 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <Building2 className="w-10 h-10 text-orange-600 dark:text-orange-400" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Cómo Agregar Mis Proyectos
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Sigue estos pasos simples para comenzar a gestionar tus proyectos inmobiliarios en nuestra plataforma.
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="space-y-8">
            {steps.map((step, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 sm:p-8 border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-shadow"
              >
                <div className="flex flex-col sm:flex-row items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center text-orange-600 dark:text-orange-400">
                      {step.icon}
                    </div>
                    <div className="mt-4 text-center sm:text-left">
                      <span className="text-2xl font-bold text-gray-400 dark:text-gray-500">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4 text-lg">
                      {step.description}
                    </p>
                    {index === 0 && (
                      <a
                        href={`${dashboardUrl}/registro`}
                        className="inline-flex items-center px-6 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors"
                      >
                        {step.action}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </a>
                    )}
                    {index > 0 && (
                      <a
                        href={`${dashboardUrl}/iniciar-sesion`}
                        className="inline-flex items-center px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        Acceder al Dashboard
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-800">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
            Características de la Plataforma
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-4 rounded-lg bg-gray-50 dark:bg-gray-700"
              >
                <CheckCircle className="w-6 h-6 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-700 dark:text-gray-300 text-lg">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-orange-600 to-orange-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            ¿Necesitas Ayuda?
          </h2>
          <p className="text-xl text-orange-50 mb-8">
            Nuestro equipo está listo para ayudarte a comenzar. Contáctanos y te guiaremos en el proceso.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`${brandConfig.clientHomeUrl}#contact`}
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-orange-600 rounded-xl font-semibold text-lg hover:bg-orange-50 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <Mail className="w-5 h-5 mr-2" />
              Contáctanos
            </a>
            <a
              href={`${dashboardUrl}/iniciar-sesion`}
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white border-2 border-white rounded-xl font-semibold text-lg hover:bg-white/10 transition-all duration-200"
            >
              Iniciar Sesión
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HowToAddProjects;

